import { useMemo, useState } from "react";
import { Scale, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, getApiError } from "@/api/client";
import { ActionModal } from "@/components/ActionModal";
import { PageHeader } from "@/components/PageHeader";
import { TakedownCard } from "@/components/TakedownCard";
import { cleanModerationPayload, moderationFields, takedownModeMessage } from "@/utils/moderation";

const TYPES = [
  { type: "post", title: "Feed post", description: "Remove a post by its production ID." },
  { type: "replay", title: "Replay", description: "Remove a saved live replay by ID." },
  { type: "clip", title: "Clip", description: "Remove a short-form clip by ID." },
  { type: "file", title: "Media file", description: "Null references and remove an object path." },
];
const NO_FIELDS = [];

export default function TakedownsView() {
  const [identifiers, setIdentifiers] = useState({ post: "", replay: "", clip: "", file: "" });
  const [request, setRequest] = useState(null); const [busy, setBusy] = useState(false); const [error, setError] = useState("");
  const fields = useMemo(() => moderationFields(), []);

  const openReview = (type, childSafety = false) => {
    const identifier = identifiers[type].trim();
    if (type === "file" && !identifier.startsWith("auralive/")) {
      toast.error("File path must start with auralive/");
      return;
    }
    setError(""); setRequest({ type, identifier, childSafety });
  };

  const remove = async (values) => {
    setBusy(true); setError("");
    const body = request.childSafety
      ? { reason: "child_safety", legal_hold: true, note: "Child-safety / CSAE escalation" }
      : cleanModerationPayload(values);
    const path = request.type === "file"
      ? `/admin/files/${request.identifier.split("/").map(encodeURIComponent).join("/")}`
      : `/admin/${request.type}s/${encodeURIComponent(request.identifier)}`;
    try {
      const { data } = await api.delete(path, { data: body });
      toast.success(takedownModeMessage(data));
      setIdentifiers((current) => ({ ...current, [request.type]: "" }));
      setRequest(null);
    } catch (requestError) {
      const message = getApiError(requestError);
      setError(requestError?.response?.status === 409 ? `Legal hold conflict: ${message}` : message);
      toast.error(message);
    } finally { setBusy(false); }
  };

  return (
    <div className="page">
      <PageHeader eyebrow="Enforcement center" title="Content takedowns" description="Remove production content with explicit reasons, evidence preservation, and an auditable record." />
      <section className="legal-hold-band" data-testid="legal-hold-information">
        <ShieldAlert size={24} /><div><strong>Evidence-aware removal</strong><p>Child-safety, CSAE, sexual exploitation, and manual legal-hold cases are hidden from feeds while evidence remains preserved for authorized review.</p></div><Scale size={28} />
      </section>
      <section className="takedown-grid">
        {TYPES.map((item) => <TakedownCard key={item.type} {...item} value={identifiers[item.type]} onValueChange={(value) => setIdentifiers({ ...identifiers, [item.type]: value })} onReview={() => openReview(item.type)} onChildSafety={() => openReview(item.type, true)} />)}
      </section>
      <ActionModal
        open={!!request}
        title={request?.childSafety ? `Escalate ${request?.type} for child safety` : `Remove ${request?.type}`}
        description={request?.childSafety ? "This immediately hides the content and automatically preserves evidence under legal hold." : `Confirm removal of ${request?.identifier}.`}
        confirmLabel={request?.childSafety ? "Escalate and preserve" : "Remove content"}
        tone="danger"
        fields={request?.childSafety ? NO_FIELDS : fields}
        notice={request?.childSafety ? "Reason: child_safety · Legal hold: enabled" : "Removal mode is selected by the production API from the reason and legal-hold settings."}
        error={error}
        onClose={() => { setRequest(null); setError(""); }}
        onConfirm={remove}
        busy={busy}
      />
    </div>
  );
}